import {
	AddBarcode,
	AztecBarcode,
	CodabarBarcode,
	Code128Barcode,
	Code39Barcode,
	DataMatrixBarcode,
	Ean13Barcode,
	Ean8Barcode,
	ItfBarcode,
	Parameter,
	Pdf417Barcode,
	QrBarcode,
	Rectangle,
	UpcaBarcode
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../helper/FunctionHelper";
import {PortalPdf417Barcode} from "./PortalPdf417Barcode";
import {PAGERANGE_ALL} from "../../portal/document/PageSelection";
import LengthUnit from "../units/LengthUnit";

export enum SwissQrCodePosition {
	top = "TOP",
	center = "CENTER",
	bottom = "BOTTOM"
}

export enum PortalBarcodeFormat {
	aztec = "azteccode",
	codabar = "rationalizedCodabar",
	code128 = "code128",
	code39 = "code39",
	datamatrix = "datamatrix",
	ean13 = "ean13",
	ean8 = "ean8",
	itf = "itf14",
	pdf417 = "pdf417",
	qrcode = "qrcode",
	upca = "upca",
	swissqrcode = "swissqrcode"
}

export class PortalAddBarcode extends AddBarcode implements Parameter {
	public format?: PortalBarcodeFormat;
	public fileName?: string;
	public position?: SwissQrCodePosition;
	public unit?: LengthUnit;

	constructor(data: any) {
		super(data);

		this.pdf417 = FunctionHelper.applyValue(data.pdf417, AddBarcode.getPdf417Default()).map(
			PortalPdf417Barcode.fromJson
		);
		this.format = FunctionHelper.exists(data?.format) ? data.format : PortalBarcodeFormat.qrcode;
		this.fileName = FunctionHelper.exists(data?.fileName) ? data.fileName : "";
		this.position = FunctionHelper.exists(data?.position) ? data.position : SwissQrCodePosition.top;
		this.unit = FunctionHelper.exists(data?.unit) ? data.unit : LengthUnit.MILLIMETER;
	}

	public static getFormatDefault(): PortalBarcodeFormat {
		return PortalBarcodeFormat.qrcode;
	}

	public static getFileNameDefault(): string {
		return "";
	}

	public static getPositionDefault(): SwissQrCodePosition {
		return SwissQrCodePosition.top;
	}

	public static getUnitDefault(): LengthUnit {
		return LengthUnit.MILLIMETER;
	}

	public static fromJson(data: any): PortalAddBarcode {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new PortalAddBarcode(data);
	}

	public toJson(): any {
		return {
			...super.toJson(),
			'format': this.format,
			'fileName': this.fileName,
			'position': this.position,
			'unit': this.unit
		};
	}

	public clone(): PortalAddBarcode {
		return PortalAddBarcode.fromJson(this.toJson());
	}

	public getFormatMarginMin(): number {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return AztecBarcode.getMarginMin();
			case PortalBarcodeFormat.codabar:
				return CodabarBarcode.getMarginMin();
			case PortalBarcodeFormat.code39:
				return Code39Barcode.getMarginMin();
			case PortalBarcodeFormat.code128:
				return Code128Barcode.getMarginMin();
			case PortalBarcodeFormat.datamatrix:
				return DataMatrixBarcode.getMarginMin();
			case PortalBarcodeFormat.ean8:
				return Ean8Barcode.getMarginMin();
			case PortalBarcodeFormat.ean13:
				return Ean13Barcode.getMarginMin();
			case PortalBarcodeFormat.itf:
				return ItfBarcode.getMarginMin();
			case PortalBarcodeFormat.pdf417:
				return Pdf417Barcode.getMarginMin();
			case PortalBarcodeFormat.qrcode:
				return QrBarcode.getMarginMin();
			case PortalBarcodeFormat.upca:
				return UpcaBarcode.getMarginMin();
			default:
				return 0;
		}
	}

	public getFormatWidth(): number {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.position?.width, Rectangle.getWidthDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.position?.width, Rectangle.getWidthDefault());
			default:
				return Rectangle.getWidthDefault();
		}
	}

	public getFormatHeight(this: PortalAddBarcode): number {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.position?.height, Rectangle.getHeightDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.position?.height, Rectangle.getHeightDefault());
			default:
				return Rectangle.getHeightDefault();
		}
	}

	public getFormatMargin(this: PortalAddBarcode): number {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.margin, AztecBarcode.getMarginDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.margin, CodabarBarcode.getMarginDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.margin, Code39Barcode.getMarginDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.margin, Code128Barcode.getMarginDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.margin, DataMatrixBarcode.getMarginDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.margin, Ean8Barcode.getMarginDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.margin, Ean13Barcode.getMarginDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.margin, ItfBarcode.getMarginDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.margin, Pdf417Barcode.getMarginDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.margin, QrBarcode.getMarginDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.margin, UpcaBarcode.getMarginDefault());
			default:
				return UpcaBarcode.getMarginDefault();
		}
	}

	public getFormatRotation(this: PortalAddBarcode): number {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.rotation, AztecBarcode.getRotationDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.rotation, CodabarBarcode.getRotationDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.rotation, Code39Barcode.getRotationDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.rotation, Code128Barcode.getRotationDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.rotation, DataMatrixBarcode.getRotationDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.rotation, Ean8Barcode.getRotationDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.rotation, Ean13Barcode.getRotationDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.rotation, ItfBarcode.getRotationDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.rotation, Pdf417Barcode.getRotationDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.rotation, QrBarcode.getRotationDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.rotation, UpcaBarcode.getRotationDefault());
			default:
				return 0;
		}
	}

	public getFormatPages(): string {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.pages, AztecBarcode.getPagesDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.pages, CodabarBarcode.getPagesDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.pages, Code39Barcode.getPagesDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.pages, Code128Barcode.getPagesDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.pages, DataMatrixBarcode.getPagesDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.pages, Ean8Barcode.getPagesDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.pages, Ean13Barcode.getPagesDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.pages, ItfBarcode.getPagesDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.pages, Pdf417Barcode.getPagesDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.pages, QrBarcode.getPagesDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.pages, UpcaBarcode.getPagesDefault());
			default:
				return PAGERANGE_ALL;
		}
	}

	public getFormatContent(this: PortalAddBarcode): string {
		switch (this.format) {
			case PortalBarcodeFormat.aztec:
				return FunctionHelper.applyValue(this.aztec![0]?.value, AztecBarcode.getValueDefault());
			case PortalBarcodeFormat.codabar:
				return FunctionHelper.applyValue(this.codabar![0]?.value, CodabarBarcode.getValueDefault());
			case PortalBarcodeFormat.code39:
				return FunctionHelper.applyValue(this.code39![0]?.value, Code39Barcode.getValueDefault());
			case PortalBarcodeFormat.code128:
				return FunctionHelper.applyValue(this.code128![0]?.value, Code128Barcode.getValueDefault());
			case PortalBarcodeFormat.datamatrix:
				return FunctionHelper.applyValue(this.datamatrix![0]?.value, DataMatrixBarcode.getValueDefault());
			case PortalBarcodeFormat.ean8:
				return FunctionHelper.applyValue(this.ean8![0]?.value, Ean8Barcode.getValueDefault());
			case PortalBarcodeFormat.ean13:
				return FunctionHelper.applyValue(this.ean13![0]?.value, Ean13Barcode.getValueDefault());
			case PortalBarcodeFormat.itf:
				return FunctionHelper.applyValue(this.itf![0]?.value, ItfBarcode.getValueDefault());
			case PortalBarcodeFormat.pdf417:
				return FunctionHelper.applyValue(this.pdf417![0]?.value, Pdf417Barcode.getValueDefault());
			case PortalBarcodeFormat.qrcode:
				return FunctionHelper.applyValue(this.qrcode![0]?.value, QrBarcode.getValueDefault());
			case PortalBarcodeFormat.upca:
				return FunctionHelper.applyValue(this.upca![0]?.value, UpcaBarcode.getValueDefault());
			case PortalBarcodeFormat.swissqrcode:
				return FunctionHelper.applyValue(this.qrswiss![0]?.content?.value, "");
			default:
				return "";
		}
	}
}